import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Pagination, Button, Checkbox } from "@mui/material";
import DataTable from "../../common/DataTable";
import SearchBar from "../../common/filters/SearchBar";
import CustomDropdown from "../../common/CustomDropdown";
import {
  fetchSalesReps,
  fetchCustomers,
  bulkAssignSalesRepAPI,
} from "../../api/customerAPI";
import {
  containerStyle,
  searchAndSortContainer,
  sortByContainer,
  sortBySelectStyle,
  paginationContainerStyle,
  paginationCaptionStyle,
  customerNameStyle,
  dropdownContainer,
  buttonStyle,
} from "./AssigningOutlets.styles";
import { Pagination as PaginationType, SalesRep } from "../../constants/types";
import { FormData } from "../../types/FormData";
import colors from "../../theme/Colours";
import Toast from "../../common/Toast";
import { getCustomersAPI } from "../../api/getCustomersAPI";

const AssigningOutlets = () => {
  const orgId = localStorage.getItem("orgId") || "";
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<FormData[]>([]);
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSalesRepId, setSelectedSalesRepId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [pagination, setPagination] = useState<PaginationType>({
    currentPage: "1",
    pageSize: "10",
    totalItems: 0,
    totalPages: 1,
  });
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [assignmentFilter, setAssignmentFilter] = useState("all");

  const firstRender = useRef(true);
  const isAllSelected =
    selectAllPages ||
    (customers.length > 0 &&
      customers.every((customer) => selectedCustomers.includes(customer.id)));

  const isIndeterminate =
    !selectAllPages && selectedCustomers.length > 0 && !isAllSelected;
  const handleSelectAll = () => {
    if (selectAllPages) {
      setSelectAllPages(false);
      setSelectedCustomers([]);
    } else {
      setSelectAllPages(true);
      setSelectedCustomers([]);
    }
  };

  const columns = [
    {
      label: (
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={handleSelectAll}
          sx={{
            "&.Mui-checked": { color: colors.primaryButtonOrange },
            "&.MuiCheckbox-indeterminate": {
              color: colors.primaryButtonOrange,
            },
          }}
        />
      ),
      field: "checkbox",
      cellStyle: { width: "8%" },
      align: "center" as const,
    },
    {
      label: "Name",
      field: "name",
      cellStyle: { width: "15%" },
    },
    {
      label: "Sales Rep",
      field: "salesRep",
      cellStyle: { width: "15%" },
    },
    {
      label: "Email",
      field: "email",
      cellStyle: { width: "28%" },
    },
    {
      label: "Phone",
      field: "phone",
      cellStyle: { width: "20%" },
    },
    {
      label: "Created Date",
      field: "createdDate",
      cellStyle: { width: "22%" },
    },
  ];

  useEffect(() => {
    const loadSalesReps = async () => {
      try {
        const reps = await fetchSalesReps();

        const normalized: SalesRep[] = reps.map((rep: any) => ({
          ...rep,
          id: rep.id ?? "",
          name:
            rep.name ||
            `${rep.firstName ?? ""} ${rep.lastName ?? ""}`.trim() ||
            "N/A",
          firstName: rep.firstName ?? "",
          lastName: rep.lastName ?? "",
        }));

        setSalesReps(normalized);
      } catch (err) {
        console.error("Failed to load sales reps", err);
      }
    };

    loadSalesReps();
  }, []);

  const loadCustomers = useCallback(
    async (
      search: string,
      page: number,
      limit: number,
      assignment?: string
    ) => {
      try {
        const {
          customers: apiCustomers,
          totalRecords,
          totalPages,
        } = await getCustomersAPI(search, page, limit, assignment);

        const formatted = apiCustomers.map((customer: any) => ({
          id: customer.id,
          firstName: customer.outletName ?? "N/A",
          salesRep: customer.assignedSalesRep?.salesRep?.name ?? "N/A",
          email: customer.emailId ?? "N/A",
          phoneNumber: customer.phoneNumber || "N/A",
          formatedCreatedAt: customer.createdAt
            ? new Date(customer.createdAt).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A",
        }));

        setCustomers(formatted as FormData[]);
        setPagination((prev) => ({
          ...prev,
          totalItems: totalRecords,
          totalPages: totalPages,
        }));
      } catch (err) {
        console.error("Failed to load customers", err);
        setCustomers([]);
        setError("Failed to load customers");
      }
    },
    []
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadCustomers(
        searchTerm,
        Number(pagination.currentPage),
        Number(pagination.pageSize),
        assignmentFilter
      );
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    searchTerm,
    pagination.currentPage,
    pagination.pageSize,
    loadCustomers,
    assignmentFilter,
  ]);

  const toggleCustomer = (id: string) => {
    if (selectAllPages) {
      setSelectAllPages(false);
      setSelectedCustomers(customers.map((c) => c.id).filter((c) => c !== id));
    } else {
      setSelectedCustomers((prev) =>
        prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
      );
    }
  };

  const handleAssign = async () => {
    if (!selectedSalesRepId) return;

    try {
      setAssigning(true);

      let customerIds = selectedCustomers;

      if (selectAllPages) {
        const allCustomers = await getCustomersAPI(searchTerm, 1, 99999);
        customerIds = allCustomers.customers.map((c: any) => c.id);
      }

      await bulkAssignSalesRepAPI(selectedSalesRepId, customerIds);

      setToastMessage("Outlets assigned successfully");
      setShowToast(true);

      setSelectedCustomers([]);
      setSelectAllPages(false);
      setSelectedSalesRepId("");

      setTimeout(() => {
        navigate(`/maltora-seller/sales-rep-management`);
      }, 1200);
    } catch (error) {
      setToastMessage("Failed to assign outlets");
      setShowToast(true);
      setError("Failed to assign outlets");
    } finally {
      setAssigning(false);
    }
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: String(newPage) }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value.toLowerCase());
    setPagination((prev) => ({ ...prev, currentPage: "1" }));
  };

  const page = Number(pagination.currentPage);
  const rowsPerPage = Number(pagination.pageSize);
  const totalRows = pagination.totalItems;

  const startIndex = (page - 1) * rowsPerPage + 1;
  const endIndex =
    customers.length === 0
      ? 0
      : Math.min(startIndex + customers.length - 1, totalRows);

  const tableData = customers.map((customer) => ({
    checkbox: (
      <Checkbox
        checked={selectAllPages || selectedCustomers.includes(customer.id)}
        onChange={() => toggleCustomer(customer.id)}
        sx={{ "&.Mui-checked": { color: colors.primaryButtonOrange } }}
      />
    ),

    name: <Typography sx={customerNameStyle}>{customer.firstName}</Typography>,
    salesRep: (
      <Typography sx={{ color: "#666", fontSize: "14px" }}>
        {customer.salesRep || "N/A"}
      </Typography>
    ),
    email: (
      <Typography sx={{ color: "#666", fontSize: "14px" }}>
        {customer.email}
      </Typography>
    ),

    phone: (
      <Typography sx={{ color: "#666", fontSize: "14px" }}>
        {customer.phoneNumber || "N/A"}
      </Typography>
    ),

    createdDate: (
      <Typography sx={{ color: "#666", fontSize: "14px" }}>
        {customer.formatedCreatedAt || "N/A"}
      </Typography>
    ),
  }));

  const salesRepOptions = salesReps.map((rep) => ({
    value: rep.id ?? "",
    label: <>{rep.name ?? `${rep.firstName} ${rep.lastName}`}</>,
  }));

  const assignmentOptions = [
    { value: "all", label: "All" },
    { value: "assigned", label: "Assigned" },
    { value: "unassigned", label: "Unassigned" },
  ];

  return (
    <>
      <Box sx={searchAndSortContainer}>
        <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />
        <Box sx={dropdownContainer}>
          <Box sx={sortByContainer}>
            <CustomDropdown
              label="Assignment"
              value={assignmentFilter}
              options={assignmentOptions}
              onChange={(value) => {
                setAssignmentFilter(value);
                setPagination((prev) => ({ ...prev, currentPage: "1" }));
              }}
              customStyles={{
                button: {
                  ...(typeof sortBySelectStyle === "object"
                    ? sortBySelectStyle
                    : {}),
                  boxShadow: "none",
                  backgroundColor: "transparent",
                  border: `1px solid ${colors.searchInputBorder}`,
                } as React.CSSProperties,
              }}
            />
          </Box>
          <Box sx={sortByContainer}>
            <CustomDropdown
              label="Sales Rep"
              value={selectedSalesRepId}
              options={salesRepOptions}
              onChange={(value) => setSelectedSalesRepId(value)}
              customStyles={{
                button: {
                  ...(typeof sortBySelectStyle === "object"
                    ? sortBySelectStyle
                    : {}),
                  boxShadow: "none",
                  backgroundColor: "transparent",
                  border: `1px solid ${colors.searchInputBorder}`,
                } as React.CSSProperties,
              }}
            />
          </Box>
          <Button
            variant="contained"
            disabled={
              !selectedSalesRepId ||
              (!selectAllPages && selectedCustomers.length === 0)
            }
            onClick={handleAssign}
            sx={buttonStyle}
          >
            Assign
          </Button>
        </Box>
      </Box>

      <Box sx={containerStyle}>
        {customers.length === 0 ? (
          <Typography align="center" py={4}>
            {error || "No customers available"}
          </Typography>
        ) : (
          <DataTable columns={columns} data={tableData} />
        )}
      </Box>

      {/* Pagination outside the container, just like in DisplayCustomers */}
      {customers.length > 0 && (
        <Box sx={paginationContainerStyle}>
          <Typography variant="subtitle2" sx={paginationCaptionStyle}>
            Showing data {startIndex} to {endIndex} of {totalRows} entries
          </Typography>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={(_, newPage) => handleChangePage(_, newPage)}
            shape="rounded"
            variant="outlined"
          />
        </Box>
      )}
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={!!error}
      />
    </>
  );
};

export default AssigningOutlets;
